import type { TimestampLike } from '../types/pplTalk';

/**
 * Safely convert any timestamp-like value to a Date object.
 * Handles Firestore Timestamps, epoch numbers, ISO strings, and Date objects.
 */
export const toDateSafe = (timestamp: TimestampLike): Date | null => {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return isNaN(timestamp.getTime()) ? null : timestamp;
  if (typeof timestamp === 'object' && 'toDate' in timestamp && typeof timestamp.toDate === 'function') {
    const d = timestamp.toDate();
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof timestamp === 'object' && 'seconds' in timestamp && typeof timestamp.seconds === 'number') {
    const d = new Date(timestamp.seconds * 1000);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof timestamp === 'number') {
    const d = new Date(timestamp);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof timestamp === 'string') {
    const d = new Date(timestamp);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

/**
 * Format a timestamp into a human-readable relative time string.
 */
export const formatTimestamp = (timestamp: TimestampLike): string => {
  const d = toDateSafe(timestamp);
  if (!d) return '';
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  if (isNaN(diffMs)) return '';
  const diffInMinutes = Math.floor(diffMs / (1000 * 60));
  if (diffInMinutes < 1) return 'just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
  if (diffInMinutes < 43200) return `${Math.floor(diffInMinutes / 1440)}d ago`;
  return d.toLocaleDateString();
};
